// Module-level cache of URLs we've already kicked off a preload for, so re-expanding a group (or
// expanding groups that share visuals) never re-fetches the same image.
const preloadedUrls = new Set<string>();

const DEFAULT_BATCH_SIZE = 5;

/**
 * Preloads image URLs into the browser cache in sequential batches of at most `batchSize`. Each batch
 * waits for all its images to settle (load or error) before the next batch starts, so we never have
 * more than `batchSize` requests in flight at once. URLs already preloaded are skipped.
 */
export function preloadImages(urls: string[], batchSize: number = DEFAULT_BATCH_SIZE): void {
    if (typeof window === 'undefined') return;

    const pending = urls.filter((url) => url && !preloadedUrls.has(url));
    if (pending.length === 0) return;
    pending.forEach((url) => preloadedUrls.add(url));

    let index = 0;
    const loadNextBatch = () => {
        const batch = pending.slice(index, index + batchSize);
        if (batch.length === 0) return;
        index += batchSize;

        let remaining = batch.length;
        const onSettled = () => {
            remaining -= 1;
            if (remaining === 0) loadNextBatch();
        };

        batch.forEach((url) => {
            const img = new Image();
            img.onload = onSettled;
            img.onerror = onSettled;
            img.src = url;
        });
    };

    loadNextBatch();
}
